/**
 * Lecture de l'acheminement d'un mail de facture.
 *
 * Le webhook Mailgun recopie sur la ligne d'envoi le dernier événement reçu
 * (`statut_mailgun`) et les horodatages qui comptent (`remis_le`, `ouvert_le`).
 * Deux pièges que ce module absorbe :
 *
 * - `statut_mailgun` est le DERNIER événement, pas un état cumulé : un
 *   `delivered` arrivé après un `opened` l'écrase. L'ouverture se lit donc sur
 *   `ouvert_le`, jamais sur `statut_mailgun`.
 * - l'énumération n'est pas fermée : Mailgun peut pousser d'autres événements
 *   que ceux abonnés aujourd'hui, d'où un libellé de repli.
 *
 * Tant que Mailgun n'a rien poussé, tout est null/0 : l'écran retombe sur
 * « Envoyée », l'état d'avant le webhook.
 */

const ECHECS = ['failed', 'rejected']

/** Horodatage court « jj/mm hh:mm », tolérant au MySQL comme à l'ISO. */
export function dateCourte (horodatage) {
  if (!horodatage) return ''
  const correspondance = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}:\d{2})/.exec(horodatage)
  return correspondance
    ? correspondance[3] + '/' + correspondance[2] + ' ' + correspondance[4]
    : String(horodatage).slice(0, 16)
}

/**
 * Le plus récent des envois d'une facture.
 * La relation Eloquent arrive triée par id décroissant, mais on ne s'y fie pas :
 * une facture réexpédiée après un échec doit montrer l'envoi réussi.
 */
export function dernierEnvoi (facture) {
  const envois = ((facture && facture.envois) || []).slice()
  if (!envois.length) return null
  envois.sort(function (a, b) { return (b.id || 0) - (a.id || 0) })
  return envois[0]
}

/**
 * État affichable d'un envoi : {code, libelle, classe, icone, detail, titre}.
 * Les icônes sont choisies dans Font Awesome 4.4, la version chargée par
 * index.html — fa-envelope-open-o (4.7) n'y existe pas.
 */
export function etatEnvoi (envoi) {
  if (!envoi) {
    return {code: 'aucun', libelle: '—', classe: 'text-muted', icone: '', detail: '', titre: ''}
  }

  const statut = envoi.statut_mailgun || null
  const ouvertures = Number(envoi.ouvertures) || 0

  if (ECHECS.indexOf(statut) !== -1 || envoi.erreur) {
    const motif = envoi.erreur || 'Envoi refusé par Mailgun'
    return {
      code: 'echec',
      libelle: 'Échec',
      classe: 'text-red',
      icone: 'fa-warning',
      detail: motif,
      titre: motif
    }
  }

  if (statut === 'complained') {
    return {
      code: 'plainte',
      libelle: 'Plainte',
      classe: 'text-orange',
      icone: 'fa-ban',
      detail: dateCourte(envoi.dernier_evenement_le || envoi.remis_le || envoi.envoye_le),
      titre: 'Le destinataire a signalé ce mail comme indésirable'
    }
  }

  if (envoi.ouvert_le) {
    const horodatage = dateCourte(envoi.ouvert_le)
    return {
      code: 'ouverte',
      libelle: 'Ouverte',
      classe: 'text-green',
      icone: 'fa-envelope-o',
      detail: ouvertures > 1 ? horodatage + ' · ouverte ' + ouvertures + ' fois' : horodatage,
      titre: 'Première ouverture le ' + horodatage
    }
  }

  if (envoi.remis_le) {
    return {
      code: 'remise',
      libelle: 'Remise',
      classe: 'text-green',
      icone: 'fa-check',
      detail: dateCourte(envoi.remis_le),
      titre: 'Remise dans la boîte du destinataire, pas encore ouverte'
    }
  }

  // Reste « Envoyée » : remise à Mailgun, en attente de son retour. Un
  // événement qu'on ne sait pas qualifier est signalé en infobulle plutôt que
  // passé sous silence.
  return {
    code: 'envoyee',
    libelle: 'Envoyée',
    classe: 'text-muted',
    icone: 'fa-paper-plane-o',
    detail: dateCourte(envoi.envoye_le),
    titre: statut
      ? 'Dernier événement Mailgun : ' + statut
      : 'Remise à Mailgun, en attente de confirmation'
  }
}

/**
 * Un 422 sur l'envoi en masse refuse tout le lot. Quand le motif est le
 * plafond mensuel Mailgun, le corps porte les chiffres : les restituer évite
 * au secrétariat de deviner combien de factures il peut encore envoyer.
 */
export function detailPlafond (corps) {
  if (!corps || corps.plafond === undefined || corps.plafond === null) return null
  const plafond = Number(corps.plafond) || 0
  const envoyes = Number(corps.deja_envoyes) || 0
  const demandes = Number(corps.demandes) || 0
  const restantes = Math.max(0, plafond - envoyes)
  return envoyes + ' envoyées ce mois-ci sur un plafond de ' + plafond +
    ' : ' + demandes + ' demandées, ' + restantes + ' restantes.'
}



// WEBPACK FOOTER //
// ./src/utils/facture/envois.js